import React from 'react';
import moment from 'moment';
import { Col } from 'reactstrap';
import { VotePlaceholder } from '@core/ui';
import { determineVotes } from '@core/business-logic';
import { SMSVote } from '@core/frontend/containers';
import { useFundingPoolBalance, useFundingStages, useProjectPageConfig } from '@core/frontend/stores';
import ProgressBar from '../../components/UI/ProgressBar/ProgressBar';

/**
 * Abstimmbereich der Projekt-Detailseite im Foerdertopf-Modell (Cassiopeia).
 *
 * Foerderstand plus Abstimmzustand: vor dem Start, nach dem Ende, bei erreichter
 * Maximalfoerderung und bei leerem Foerdertopf steht ein Hinweis, sonst die SMS-Abstimmung.
 * Der Kern rendert diese Komponente als `product.projectVotePanel`; sie bringt ihre `<Col>`s
 * selbst mit, weil sie innerhalb einer `<Row>` der Detailseite landet.
 *
 * Die Fortschrittsanzeige (`ProgressBar`) bleibt App-Code: Stufen, Symbole und Farbe sind
 * der Foerdertopf (Intended Difference #1), nicht der Kern.
 */
const FundingVotePanel = ({ project, phases, ...routeProps }) => {
    const fundingStages = useFundingStages();
    const balance = useFundingPoolBalance();
    const { thresholdIconName, thresholdIconActiveName, progressBarSuccessColor } = useProjectPageConfig();

    const voteCount = determineVotes(project.voteCount, phases);
    const { voteStarted, voteEnded, voteStart } = phases;

    let voteComponent = null;

    if (!voteStarted) {
        voteComponent = <VotePlaceholder messageText={'Am ' + moment(voteStart).format('DD.MM.') + " geht's los"} />;
    } else if (voteEnded) {
        voteComponent = <VotePlaceholder messageText="Die Abstimmung ist beendet!" />;
    } else {
        const lastFundingStage = fundingStages.length > 0 ? fundingStages[fundingStages.length - 1] : null;

        if (lastFundingStage && voteCount >= lastFundingStage.threshold) {
            voteComponent = (
                <VotePlaceholder
                    messageText="Die maximale Förderung ist erreicht! Geben Sie Ihre Stimme einem anderen Projekt!"
                    messageType="success"
                    messageLink="/projekte"
                />
            );
        } else if (balance <= 0) {
            voteComponent = <VotePlaceholder messageText="Der Fördertopf ist leer" messageType="info" />;
        } else {
            voteComponent = <SMSVote projectCategory={project.projectCategory} {...routeProps} />;
        }
    }

    return (
        <>
            <Col className="overflow-hidden">
                <ProgressBar
                    cssClasses="mt-5 mb-4"
                    voteCount={voteCount}
                    projectBalance={project.fundingAccount ? project.fundingAccount.balance : 0}
                    bubble={true}
                    fundingStages={fundingStages}
                    thresholdIconName={thresholdIconName}
                    thresholdIconActiveName={thresholdIconActiveName}
                    progressBarSuccessColor={progressBarSuccessColor}
                />
            </Col>
            {voteComponent}
        </>
    );
};

export default FundingVotePanel;
